import React from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Stack from '@mui/material/Stack'
import CaretDownSvg from '../../assets/images/CaretDown.svg'
import ActivityFunctionField from './ActivityFunctionField'
import ActivitySponsorTypeField from './ActivitySponsorTypeField'
import StudyDetailsField from './StudyDetailsField'
import useDisplayNames from '../../shared/hooks/useDisplayNames/useDisplayNames'
import { requiredFieldHelperText } from './requiredFieldHelperText'

interface ActivityManagementFieldProps {
    sponsorType: string
    onSponsorTypeChange: (value: string) => void
    isFunction: string
    onFunctionChange: (value: string) => void
    affiliateName: string
    onAffiliateNameChange: (value: string) => void
    isVendor: boolean
    onIsVendorChange: (checked: boolean) => void
    vendorDetails: string
    onVendorDetailsChange: (value: string) => void
    corporateOrRnDGoal: boolean
    onCorporateOrRnDGoalChange: (value: boolean) => void
    studyOwner: string
    onStudyOwnerChange: (value: string) => void
    // Edit-only: GSL/Planisware provider field
    providerName?: string
    onProviderNameChange?: (value: string) => void
    disabled?: boolean
    existingFunction?: string
    existingSponsorType?: string
    // Keyed by the org's required-fields config (e.g. 'sponsorType', 'affiliateName', 'studyOwner', 'providerName')
    requiredFields?: string[]
    fieldErrors?: Record<string, string>
}

const ActivityManagementField: React.FC<ActivityManagementFieldProps> = ({
    sponsorType,
    onSponsorTypeChange,
    isFunction,
    onFunctionChange,
    affiliateName,
    onAffiliateNameChange,
    isVendor,
    onIsVendorChange,
    vendorDetails,
    onVendorDetailsChange,
    corporateOrRnDGoal,
    onCorporateOrRnDGoalChange,
    studyOwner,
    onStudyOwnerChange,
    providerName,
    onProviderNameChange,
    disabled,
    existingFunction,
    existingSponsorType,
    requiredFields,
    fieldErrors,
}) => {
    const [expanded, setExpanded] = React.useState(true)

    // Auto-expand this section when a failed submit flags one of its fields, so a missing required
    // field can't hide inside a collapsed section. Keyed on a stable boolean so the effect only
    // fires on the false→true transition — it won't re-open a section the user collapses while the
    // error is still unresolved.
    const hasMissingField = [
        'sponsorType',
        'function',
        'affiliateName',
        'vendorDetails',
        'studyOwner',
        'providerName',
    ].some((f) => !!fieldErrors?.[f])
    React.useEffect(() => {
        if (hasMissingField) setExpanded(true)
    }, [hasMissingField])
    const { getDisplayName } = useDisplayNames()
    const providerNameLabel = getDisplayName('providerName', 'Provider Name')

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            disableGutters
            elevation={0}
            sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
            <AccordionSummary
                expandIcon={
                    <Box
                        component="img"
                        src={CaretDownSvg}
                        sx={{
                            width: 20,
                            height: 20,
                            transition: 'transform 0.2s',
                            transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                        }}
                    />
                }
                sx={{
                    px: 0,
                    py: 1,
                    backgroundColor: 'inherit',
                    '& .MuiAccordionSummary-expandIconWrapper': { transform: 'rotate(0deg)' },
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(180deg)' },
                }}
            >
                <Typography variant="18x600">Management</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1 }}>
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                Sponsor Type
                                {requiredFields?.includes('sponsorType') && (
                                    <Typography component="span" sx={{ color: 'error.main' }}>
                                        {' '}
                                        *
                                    </Typography>
                                )}
                            </Typography>
                            <ActivitySponsorTypeField
                                value={sponsorType}
                                onChange={onSponsorTypeChange}
                                disabled={disabled}
                                existingValue={existingSponsorType}
                                error={!!fieldErrors?.sponsorType}
                                helperText={fieldErrors?.sponsorType}
                                required={requiredFields?.includes('sponsorType')}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                Function
                                {requiredFields?.includes('function') && (
                                    <Typography component="span" sx={{ color: 'error.main' }}>
                                        {' '}
                                        *
                                    </Typography>
                                )}
                            </Typography>
                            <ActivityFunctionField
                                value={isFunction}
                                onChange={onFunctionChange}
                                existingValue={existingFunction}
                                error={!!fieldErrors?.function}
                                helperText={fieldErrors?.function}
                                required={requiredFields?.includes('function')}
                            />
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Affiliate
                            {requiredFields?.includes('affiliateName') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Affiliate"
                            value={affiliateName}
                            disabled={disabled}
                            error={!!fieldErrors?.affiliateName}
                            helperText={requiredFieldHelperText('affiliateName', requiredFields, fieldErrors)}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) => onAffiliateNameChange(e.target.value)}
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Corporate or R&amp;D Goal
                        </Typography>
                        {/* Internal classification — editable even for Planisware activities,
                            so intentionally not gated by `disabled`. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">No</Typography>
                            <Switch
                                checked={corporateOrRnDGoal}
                                onChange={(e) => onCorporateOrRnDGoalChange(e.target.checked)}
                                name="corporateOrRnDGoal"
                            />
                            <Typography variant="body2">Yes</Typography>
                        </Box>
                    </Box>
                    <StudyDetailsField
                        isVendor={isVendor}
                        onChange={onIsVendorChange}
                        vendorDetails={vendorDetails}
                        onVendorDetailsChange={onVendorDetailsChange}
                        disabled={disabled}
                        vendorDetailsRequired={requiredFields?.includes('vendorDetails')}
                        vendorDetailsError={fieldErrors?.vendorDetails}
                    />
                    {providerName !== undefined && onProviderNameChange && (
                        <Box>
                            <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                {providerNameLabel}
                                {requiredFields?.includes('providerName') && (
                                    <Typography component="span" sx={{ color: 'error.main' }}>
                                        {' '}
                                        *
                                    </Typography>
                                )}
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder={`Enter ${providerNameLabel}`}
                                value={providerName}
                                disabled={disabled}
                                error={!!fieldErrors?.providerName}
                                helperText={requiredFieldHelperText('providerName', requiredFields, fieldErrors)}
                                onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                onChange={(e) => onProviderNameChange(e.target.value)}
                                sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                            />
                        </Box>
                    )}
                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Study Contact
                            {requiredFields?.includes('studyOwner') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Study Contact"
                            value={studyOwner}
                            disabled={disabled}
                            error={!!fieldErrors?.studyOwner}
                            helperText={requiredFieldHelperText('studyOwner', requiredFields, fieldErrors)}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) => onStudyOwnerChange(e.target.value)}
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

export default ActivityManagementField
